import { useCallback, useState } from 'react';
import { createFileRoute, Navigate, useRouter } from '@tanstack/react-router';
import { useQuery } from '@apollo/client';
import { Text } from 'react-aria-components';
import PageLoader from 'src/components/ui/PageLoader';
import PageTitle from 'src/components/creator/PageTitle';
import Button from 'src/components/ui/Button';
import PreviewField from 'src/components/creator/PreviewField';
import PaperIcon from 'src/assets/icons/file-paper-document.svg';
import PUBLICATION_QUERY from 'src/graphql/query/genie-publication.graphql';

import type { Publication } from 'src/types/publication';

const PublicationIndexPage = () => {
  const { publicationId } = Route.useParams();
  const { history, navigate } = useRouter();
  const { data, loading, error } = useQuery<{ publication: Publication }>(
    PUBLICATION_QUERY,
    {
      variables: { publicationId: Number(publicationId) },
    }
  );

  if (loading) return <PageLoader />;
  if (!data?.publication || error) return <Navigate to="/out-of-print" />;

  return (
    <>
      <PageTitle
        title={`Genie Publication: ${data.publication.publicationTitle}`}
      />
      <div className="flex items-center gap-2 px-4 pt-6">
        <Button variant="secondary" onPress={() => history.back()}>
          Back
        </Button>
      </div>
      <div className="relative overflow-y-auto bg-surface-alt rounded-xl shadow-3 mt-4 px-6 min-h-0 flex-1">
        <div className="w-full max-w-[800px] mx-auto mt-11 mb-20">
          <Text
            elementType="p"
            className="font-medium text-base text-surface-alt-90 text-center mb-6"
          >
            This title{' '}
            {data.publication.isPublishedInOopLibrary ? 'was located and ' : ''}
            may be available. <br />
            Please create an order, using the citation information below, and
            LAD will work to clear permissions for you.
          </Text>

          <div className="grid grid-cols-2 gap-1">
            <PreviewField
              label="Title"
              value={data.publication.publicationTitle}
              className="col-span-full"
            />
            {data.publication.authors.map((author, index) => (
              <PreviewField
                className="col-span-full"
                key={author.authorId}
                label={index > 0 ? `Author ${index + 1}` : 'Author'}
                value={[author.firstName, author.lastName].join(' ')}
              />
            ))}

            <PreviewField label="ISBN/ISSN" value={data.publication.isbnIssn} />
            <PreviewField
              label="Copyright Year"
              value={data.publication.copyrightYear}
            />
            <PreviewField
              label="Product Number"
              value={data.publication.productNumber}
            />
            <PreviewField
              label="Product Type"
              value={data.publication.productType}
            />
            <PreviewField label="Edition" value={data.publication.edition} />
            <PreviewField
              label="Public Domain?"
              value={data.publication.publicDomain ? 'YES' : 'NO'}
            />
            <PreviewField label="Website" value={data.publication.website} />
            <PreviewField label="Volume" value={data.publication.volume} />
          </div>
          <div className="flex items-center justify-end gap-4 mt-4 mb-4">
            <CopyButton publication={data.publication} />
            <Button
              onPress={() =>
                navigate({
                  from: '/out-of-print/$publicationId',
                  to: '/requests/create',
                  search: { publicationId },
                })
              }
              variant="success"
              IconLeft={PaperIcon}
            >
              Create Order
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const CopyButton = ({ publication }: { publication: Publication }) => {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    let text = `Title: ${publication.publicationTitle};`;

    if (publication.copyrightYear)
      text += ` Year: ${publication.copyrightYear};`;
    if (publication.isbnIssn) text += ` ISBN: ${publication.isbnIssn};`;
    if (publication.authors.length > 0) {
      text += ` Author: ${publication.authors
        .map((a) => [a.firstName, a.lastName].join(' '))
        .filter(Boolean)
        .join(', ')};`;
    }

    if (publication.productNumber)
      text += ` Product Number: ${publication.productNumber};`;
    if (publication.productType)
      text += ` Product Type: ${publication.productType};`;

    if (publication.volume) text += ` Volume: ${publication.volume};`;
    if (publication.website) text += ` Website: ${publication.website};`;

    await navigator.clipboard.writeText(text);

    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [publication]);

  if (!navigator?.clipboard) return null;

  return (
    <>
      <Button variant="primary" onPress={onCopy}>
        {copied ? 'Copied!' : 'Copy All'}
      </Button>
      <span role="status" className="sr-only">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </>
  );
};

export const Route = createFileRoute(
  '/_authenticated/_creator/out-of-print/$publicationId'
)({
  component: PublicationIndexPage,
});
